import React from 'react';
import rest from '../../data/restWrapper';
import dayjs from 'dayjs';
import translate from '@data/translations';
import useStore from '@data/state/zustand';
import TimePicker from '@components/timePicker';
import { CustomDayPicker } from '@components/DayPicker';
import { ReactComponent as DeleteIcon } from '../../assets/custom/icons/trash.svg';

interface Guest {
    cardCodeType: number;
    code: number;
    validToTimestamp: number;
}

const AccessCodes1 = (props: any) => {
    const remoteDeviceId = props.remoteDeviceId || props.match.params.remoteDeviceId;
    const [accessCodes, setAccessCodes] = React.useState<any>(props.roomSettings.accessCodes);
    const [duplicateCodes, setDuplicateCodes] = React.useState<Record<string, boolean>>({});
    const [modified, setModified] = React.useState<any>({
        guest1: false,
        guest2: false,
        guest3: false,
        manager: false,
        security: false,
        maid1: false,
        maid2: false,
        maid3: false,
    });

    React.useEffect(() => {
        const loadData = async () => {
            if (props.roomSettings.accessCodes) {
                let _accessCodes = { ...props.roomSettings.accessCodes };

                // Ako postoji rezervacija, postavi vrijeme za kodove koji su 0
                if (props.reservation) {
                    const checkOutDate = dayjs(props.reservation.checkOutTimestamp);
                    const checkOutTimeWithAdd2Hurs = checkOutDate.add(2, 'hours').second(0).millisecond(0).valueOf();
                    codeTypes.forEach((key) => {
                        if (_accessCodes[key] && _accessCodes[key].code === 0) {
                            _accessCodes[key].validToTimestamp = checkOutTimeWithAdd2Hurs;
                        }
                    });
                }
                setAccessCodes(_accessCodes);
            }
        };
        loadData().catch((err) => {
            console.log(err);
        });
    }, [props.roomSettings, props.remoteDeviceId, props.reservation]);

    const getRandomMilisecondsToAddFromRangeOfMinutes = (min: number, max: number) => {
        const otherSettings = useStore.getState().otherSettings;
        let maxInMiliseconds = max * 60 * 1000;
        let randomMilisecondsToAdd = otherSettings?.testModeForSimultaneouslyCardCodeExpiration
            ? 0
            : Math.floor(Math.random() * (maxInMiliseconds - min + 1)) + min;
        return randomMilisecondsToAdd;
    };

    const roundToNearest15 = (date = new Date()) => {
        const minutes = 15;
        const ms = 1000 * 60 * minutes;
        return new Date(Math.floor(date.getTime() / ms) * ms).getTime();
    };

    const onCodeChange = (type: string, e: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        _accessCodes[type].code = e.target.value ? parseInt(e.target.value, 10) : 0;

        const newDuplicateCodes: Record<string, boolean> = {};
        const entries = Object.entries(_accessCodes) as [string, Guest][];

        for (const [keyA, dataA] of entries) {
            newDuplicateCodes[keyA] = entries.some(
                ([keyB, dataB]) => keyA !== keyB && dataA.code !== 0 && dataA.code === dataB.code
            );
        }

        setAccessCodes(_accessCodes);
        setModified(_modified);
        setDuplicateCodes(newDuplicateCodes);
    };

    const handleTimeChange = (type: string, e: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        // Uzmi postojeći datum i zamijeni samo vrijeme
        const existingDate = new Date(_accessCodes[type].validToTimestamp);
        const newTime = e.toDate();
        
        existingDate.setHours(newTime.getHours());
        existingDate.setMinutes(newTime.getMinutes());
        existingDate.setSeconds(0);
        existingDate.setMilliseconds(0);

        _accessCodes[type].validToTimestamp = existingDate.getTime();
        setAccessCodes(_accessCodes);
        setModified(_modified);
    };

    const handleDateChange = (type: string, e: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        // Uzmi postojeće vrijeme iz trenutnog timestampa
        const existingDateTime = new Date(_accessCodes[type].validToTimestamp);
        const newDate = new Date(e);
        
        // Postavi novi datum ali zadrži postojeće vrijeme
        newDate.setHours(existingDateTime.getHours());
        newDate.setMinutes(existingDateTime.getMinutes());
        newDate.setSeconds(existingDateTime.getSeconds());
        newDate.setMilliseconds(existingDateTime.getMilliseconds());

        _accessCodes[type].validToTimestamp = newDate.getTime();
        setAccessCodes(_accessCodes);
        setModified(_modified);
    };

    const handleDateTimeSet = (type: string, targetTimestamp: any) => {
        let _accessCodes = { ...accessCodes };
        let _modified = { ...modified };
        _modified[type] = true;

        _accessCodes[type].validToTimestamp = targetTimestamp;
        setAccessCodes(_accessCodes);
        setModified(_modified);
    };

    const onCardCodeSave = async (cardCodeType: string) => {
        try {
            let cardCodeToSave = accessCodes[cardCodeType];
            let payload = {
                code: cardCodeToSave.code,
                validToTimestamp: cardCodeToSave.validToTimestamp,
                cardCodeType: cardCodeType,
                reservationId: props.reservation ? props.reservation.id : null,
            };

            if (props.onSaveHandler) {
                let accessCodes = props.SRCTemplateData.accessCodes;
                accessCodes[cardCodeType] = cardCodeToSave;
                props.onSaveHandler('accessCodes', accessCodes);
            } else {
                if (props.showOnlyOneSRC) {
                    if (props.remoteDeviceId1) {
                        await rest('/src/setCardCodeWithValidToTime/' + props.remoteDeviceId1, 'POST', payload);
                    }
                    if (props.remoteDeviceId2) {
                        await rest('/src/setCardCodeWithValidToTime/' + props.remoteDeviceId2, 'POST', payload);
                    }
                    if (props.remoteDeviceId3) {
                        await rest('/src/setCardCodeWithValidToTime/' + props.remoteDeviceId3, 'POST', payload);
                    }
                } else {
                    await rest('/src/setCardCodeWithValidToTime/' + remoteDeviceId, 'POST', payload);
                }
                let _modified = { ...modified };
                _modified[cardCodeType] = false;
                setModified(_modified);
            }
        } catch (err) {
            console.warn(err);
        }
    };

    const discardCode = async (cardCodeType: any) => {
        try {
            let payload = {
                code: 0,
                validToTimestamp: 0,
                cardCodeType,
                reservationId: props.reservation ? Number(props.reservation.id) : null,
            };
            await rest('/src/setCardCodeWithValidToTime/' + remoteDeviceId, 'POST', payload);

            let _accessCodes = { ...accessCodes };
            _accessCodes[cardCodeType].code = 0;
            setAccessCodes(_accessCodes);
        } catch (err) {
            console.log(err);
        }
    };

    const codeTypes = ['guest1', 'guest2', 'guest3'];

    return (
        <div className="card mt-4p ">
            <div className="custom-list-header ml-8p mr-8p">
                <div style={{ minWidth: '10%', maxWidth: '10%' }}>{translate('Type')}</div>
                <div style={{ minWidth: '23%', maxWidth: '23%' }}>{translate('Code')}</div>
                <div style={{ minWidth: '38%', maxWidth: '38%' }}>{translate('Valid to')}</div>
                <div style={{ minWidth: '23%', maxWidth: '23%' }}>{translate('Actions')}</div>
            </div>
            {codeTypes.map((key) => {
                const value = accessCodes[key];

                const _dateValue = new Date(value.validToTimestamp);
                const _today = new Date();

                const isDateValueToday =
                    _dateValue.getFullYear() === _today.getFullYear() &&
                    _dateValue.getMonth() === _today.getMonth() &&
                    _dateValue.getDate() === _today.getDate();

                const isTodaysTimePassed = isDateValueToday && value.validToTimestamp < Date.now();

                return (
                    <div className="display-flex flex-column" key={key}>
                        <div className="custom-list-item ml-8p mr-8p">
                            <div style={{ minWidth: '10%', maxWidth: '10%' }}>
                                {key === 'manager'
                                    ? translate('General access 1')
                                    : key === 'security'
                                    ? translate('General access 2')
                                    : translate(key)}
                            </div>
                            <div style={{ minWidth: '23%', maxWidth: '23%' }}>
                                <input
                                    onChange={(e) => onCodeChange(key, e)}
                                    type="text"
                                    className="input-text form-control form-control-sm accessCodes1-code-input"
                                    value={value.code}
                                />
                            </div>
                            <div style={{ minWidth: '38%', maxWidth: '38%', fontSize: '16px', position: 'relative' }}>
                                <CustomDayPicker
                                    value={new Date(value.validToTimestamp).getTime()}
                                    onSelect={(e: any) => handleDateChange(key, e)}
                                    width={'90px'}
                                    disabledBefore={new Date()}
                                    usePortal={true}
                                />
                                <TimePicker
                                    minuteStep={15}
                                    value={dayjs(value.validToTimestamp)}
                                    onChange={(e: any) => handleTimeChange(key, e)}
                                />
                                {props.reservation && (
                                    <button
                                        onClick={() => {
                                            const reservationTime = dayjs(props.reservation.checkOutTimestamp);
                                            handleDateTimeSet(key, dayjs(reservationTime).valueOf());
                                        }}
                                        className="btn btn-sm btn-warning accessCodes1-icon-btn ml-4p"
                                        title={translate('Accept current reservation expiration')}
                                    >
                                        <i className="fa fa-calendar-check-o"></i>
                                    </button>
                                )}
                            </div>
                            <div style={{ minWidth: '23%', maxWidth: '23%' }}>
                                <button
                                    disabled={!modified[key]}
                                    onClick={() => onCardCodeSave(key)}
                                    className={modified[key] ? 'button-success' : 'button-secondary-res-programming-card'}
                                >
                                    {translate('Update')}
                                </button>
                                {value.code !== 0 ? (
                                    <div className="fill-red" onClick={() => discardCode(key)}>
                                        <DeleteIcon />
                                    </div>
                                ) : (
                                    <div className="fill-red" style={{ width: '32px', height: '32px' }}></div>
                                )}
                            </div>
                        </div>
                        <div className="p-10p">
                            {' '}
                            <div className="mb-4p">
                                {duplicateCodes[key] ? (
                                    <span className="text-danger">
                                        {translate('This card code is already in use')}.
                                    </span>
                                ) : null}
                            </div>
                            {!isTodaysTimePassed && (
                                <span>
                                    {isDateValueToday
                                        ? translate(
                                              'Card code expires today. Make sure to check the expiration time to avoid losing access'
                                          ) + '.'
                                        : ''}
                                </span>
                            )}
                            <span>
                                {isTodaysTimePassed
                                    ? translate('Card code has expired. Please choose a new expiration time') + '.'
                                    : ''}
                            </span>{' '}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AccessCodes1;
